import React, { useCallback, useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";

// Default fallback center (Delhi) — used only when browser geolocation fails
const DELHI_CENTER = [28.6139, 77.209];

/**
 * Hook: tries browser geolocation once, caches result.
 * Returns a center array [lat, lng] — real location or Delhi fallback.
 * Screens should prefer live device location > this fallback > Delhi.
 */
let _cachedBrowserCenter = null;
export function useUserFallbackCenter() {
  const [center, setCenter] = useState(_cachedBrowserCenter || DELHI_CENTER);

  useEffect(() => {
    if (_cachedBrowserCenter) {
      setCenter(_cachedBrowserCenter);
      return;
    }
    if (typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const c = [pos.coords.latitude, pos.coords.longitude];
          _cachedBrowserCenter = c;
          setCenter(c);
        },
        () => {}, // silently fall back to Delhi
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 300000 }
      );
    }
  }, []);

  return center;
}

// Blue live dot
export const liveDotIcon = L.divIcon({
  className: "live-marker",
  html: '<div class="live-dot"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

export function pinIcon(color, label = "") {
  return L.divIcon({
    className: "pin-marker",
    html: `<div class="marker-pin" style="background:${color}">${label}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

export const startIcon = pinIcon("#16a34a", "A");
export const endIcon = pinIcon("#dc2626", "B");

/**
 * Keeps the map centred on the vehicle, but yields the moment the user
 * takes control.
 *
 * The live screens re-poll every 10 seconds, and an unconditional
 * setView() meant any attempt to pan or zoom away was yanked back on the
 * next tick — the map was effectively impossible to explore. Dragging or
 * zooming now releases the camera; flipping `follow` re-arms it, which is
 * what the parent's "recenter" button does.
 */
export function Recenter({ lat, lon, zoom, follow = true, onUserMove }) {
  const map = useMap();
  const released = useRef(false);

  useMapEvents({
    dragstart: () => {
      released.current = true;
      onUserMove?.();
    },
    zoomstart: () => {
      released.current = true;
      onUserMove?.();
    },
  });

  // Re-arming from the parent clears the release latch
  useEffect(() => {
    if (follow) released.current = false;
  }, [follow]);

  useEffect(() => {
    if (!follow || released.current) return;
    if (lat == null || lon == null) return;
    map.setView([lat, lon], zoom ?? map.getZoom(), { animate: true });
  }, [lat, lon, zoom, follow, map]);

  return null;
}

export function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (!positions || positions.length < 2) return;
    const bounds = L.latLngBounds(positions);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [positions, map]);
  return null;
}

/** Bridges map clicks to a plain callback — see the note in LeafletMap. */
function ClickHandler({ onClick }) {
  useMapEvents({ click: (ev) => onClick(ev.latlng) });
  return null;
}

export default function LeafletMap({
  center = DELHI_CENTER,
  zoom = 13,
  className = "",
  children,
  onClick,
  scrollWheelZoom = true,
  testid = "map-container",
}) {
  // react-leaflet v4 invokes whenReady with no arguments, so the previous
  // `whenReady={(e) => e.target.on("click", …)}` wiring never attached a
  // listener at all. useMapEvents is the supported v4 path.
  const handleClick = useCallback((latlng) => onClick?.(latlng), [onClick]);

  return (
    <div data-testid={testid} className={className} style={{ width: "100%", height: "100%" }}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ width: "100%", height: "100%" }}
        scrollWheelZoom={scrollWheelZoom}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {onClick && <ClickHandler onClick={handleClick} />}
        {children}
      </MapContainer>
    </div>
  );
}
